import React, { useState, useEffect } from 'react';
import { styled } from '../../../shared/Theme/theme';
import { Button, Input, Select, Card, Row, Col, Typography, Space, Divider, Modal, Form } from 'antd';
import { SendOutlined, PlusOutlined, PhoneOutlined, WhatsAppOutlined, MailOutlined } from '@ant-design/icons';
import countries from '../../../../static/data/countries.json';
import carriersWeightPricing from '../../../../static/data/carriers-weight-pricing.json';
import { CarrierWeightPricing } from '../../../../models/entities/admin/carrierWeightPricing';
import { CarrierPricingResponse } from '../../../../models/entities/admin/carrierPricingResponse';

const { Title, Text } = Typography;
const { Option } = Select;

interface Parcel {
    id: string;
    quantity: number;
    weight: number;
    length?: number;
    width?: number;
    height?: number;
}

interface ShippingOption {
    carrierCode: string;
    carrierName: string;
    service: string;
    estimatedDays: string;
    estimatedDate: string;
    businessPrice: number;
    privatePrice: number;
    insurance: string;
    printingRequired: boolean;
}

type Props = {
    carrierWeightPricing: CarrierWeightPricing[];
    carrierPricingResponse?: CarrierPricingResponse;
    fetchPublicCarrierWeightPricingWithDimensions: (payload: { 
        weight: number; 
        quantity: number; 
        regionId: number; 
        length?: number; 
        width?: number; 
        height?: number; 
    }) => any;
    localization: any;
};

const ShippingEstimatesPage = ({ 
    carrierWeightPricing, 
    carrierPricingResponse, 
    fetchPublicCarrierWeightPricingWithDimensions, 
    localization 
}: Props) => {
    const [form] = Form.useForm();
    const [originCountry, setOriginCountry] = useState<string>('');
    const [destinationCountry, setDestinationCountry] = useState<string>('');
    const [parcels, setParcels] = useState<Parcel[]>([{ id: '1', quantity: 1, weight: 0 }]);
    const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [showLeadModal, setShowLeadModal] = useState(false);
    const [leadForm] = Form.useForm();
    const [currentResponse, setCurrentResponse] = useState<CarrierPricingResponse | null>(null);

    useEffect(() => {
        // Check if user has already seen the modal
        const hasSeenModal = localStorage.getItem('cargozone-lead-modal-seen');

        if (!hasSeenModal) {
        const timer = setTimeout(() => {
            setShowLeadModal(true);
        }, 5000);

        return () => clearTimeout(timer);
        }
    }, []);

    const handleOriginCountryChange = (value: string) => {
        setOriginCountry(value);
        setShippingOptions([]);
        if (value === israelCode) {
            setDestinationCountry('');
        } else {
            setDestinationCountry(israelCode);
        }
    };

    const handleDestinationCountryChange = (value: string) => {
        setDestinationCountry(value);
        setShippingOptions([]);
        if (value === israelCode) {
            setOriginCountry('');
        } else {
            setOriginCountry(israelCode);
        }
    };

    const israelCountry = countries.find((country) => country.name === 'Israel');
    const israelCode = israelCountry?.code || 'IL';

    const getRegionId = (countryCode: string): number => {
        const regionMap: { [key: string]: number } = {
            DE: 0,
            FR: 0,
            IT: 0,
            ES: 0,
            NL: 0,
            BE: 0,
            AT: 0,
            CH: 0,
            SE: 0,
            NO: 0,
            DK: 0,
            FI: 0,
            PL: 0,
            CZ: 0,
            HU: 0,
            RO: 0,
            BG: 0,
            HR: 0,
            SI: 0,
            SK: 0,
            LT: 0,
            LV: 0,
            EE: 0,
            IE: 0,
            PT: 0,
            GR: 0,
            CY: 0,
            MT: 0,
            LU: 0,
            CN: 2,
            HK: 2,
            SG: 3,
            JP: 4,
            IN: 5,
            VN: 6,
            TH: 7,
            KR: 8,
            TW: 8,
            MY: 8,
            ID: 8,
            PH: 8,
            BN: 8,
            MM: 8,
            KH: 8,
            LA: 8,
            AU: 9,
            NZ: 9,
            BH: 9,
            ZA: 9,
            AR: 9,
            BR: 9,
            CL: 9,
            CO: 9,
            MX: 9,
            PE: 9,
            UY: 9
        };
        return regionMap[countryCode] || 14;
    };

    const calculateDimensionalWeight = (length: number, width: number, height: number): number => {
        return (length * width * height) / 6000;
    };

    const getTotalWeight = (parcel: Parcel): number => {
        if (parcel.length && parcel.width && parcel.height) {
            const dimensionalWeight = calculateDimensionalWeight(parcel.length, parcel.width, parcel.height);
            return Math.max(parcel.weight, dimensionalWeight);
        }
        return parcel.weight;
    };

    const totalWeight = parcels.reduce((total, parcel) => {
        const parcelTotalWeight = getTotalWeight(parcel);
        return total + parcel.quantity * parcelTotalWeight;
    }, 0);

    const handleAddParcel = () => {
        const newParcel: Parcel = {
            id: Date.now().toString(),
            quantity: 1,
            weight: 0
        };
        setParcels([...parcels, newParcel]);
    };

    const handleRemoveParcel = (id: string) => {
        if (parcels.length > 1) {
            setParcels(parcels.filter((parcel) => parcel.id !== id));
        }
    };

    const handleParcelChange = (id: string, field: keyof Parcel, value: number) => {
        setParcels(parcels.map((parcel) => (parcel.id === id ? { ...parcel, [field]: value } : parcel)));
    };

    const handleGetQuotes = async () => {
        if (!originCountry || !destinationCountry || totalWeight === 0) {
            return;
        }

        setLoading(true);
        try {
            const firstParcel = parcels[0];
            const hasDimensions = firstParcel.length && firstParcel.width && firstParcel.height;
            
            const response = await fetchPublicCarrierWeightPricingWithDimensions({
                    weight: totalWeight, 
                    quantity: parcels.length, 
                regionId: getRegionId(destinationCountry),
                length: hasDimensions ? firstParcel.length : undefined,
                width: hasDimensions ? firstParcel.width : undefined,
                height: hasDimensions ? firstParcel.height : undefined
            });
            setCurrentResponse(response);
            const options: ShippingOption[] = [];
            
            if (response?.carriers) {
                const carriersWithPricing = response.carriers.filter((carrier) => carrier.hasPricing);

                carriersWithPricing.forEach((carrier) => {
                    // Map carrier codes to service details
                    const serviceDetails: { [key: string]: { service: string; days: string; daysToAdd: number } } = {
                        '1000': { service: 'TNT Express', days: '2-5 Days', daysToAdd: 3 },
                        '1001': { service: 'TNT Economy', days: '5-7 Days', daysToAdd: 6 }
                    };

                    const details = serviceDetails[carrier.carrierCode] || {
                        service: `${carrier.carrierName} Service`,
                        days: '5-7 Days',
                        daysToAdd: 6
                    };
                    
                    options.push({
                        carrierCode: carrier.carrierCode,
                        carrierName: carrier.carrierName,
                        service: details.service,
                        estimatedDays: details.days,
                        estimatedDate: new Date(Date.now() + details.daysToAdd * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        businessPrice: carrier.totalPrice || 0,
                        privatePrice: (carrier.totalPrice || 0) * 1.3,
                        insurance: 'Up To $100',
                        printingRequired: true
                    });
                });
            }

            setShippingOptions(options);
        } catch (error) {
            console.error('Error fetching quotes:', error);
            // Set empty response to clear any previous data
            setCurrentResponse(null);
            setShippingOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleLeadSubmit = (values: { name: string; email?: string }) => {
        // Mark modal as seen in localStorage
        localStorage.setItem('cargozone-lead-modal-seen', 'true');
        setShowLeadModal(false);
        leadForm.resetFields();
    };

    const handleModalClose = () => {
        // Mark modal as seen even if user just closes it
        localStorage.setItem('cargozone-lead-modal-seen', 'true');
        setShowLeadModal(false);
    };

    const handleDirectContact = (type: 'call' | 'whatsapp' | 'email') => {
        if (type === 'call') {
            window.open('tel:+972528929199');
        } else if (type === 'whatsapp') {
            window.open('https://wa.me/972528929199');
        } else if (type === 'email') {
            window.open('mailto:info@cargozone.ai');
        }
    };

    return (
        <Container>
            <HeaderSection>
                <MainTitle>
                    <Title level={1} style={{ color: '#394372', margin: 0, fontSize: '2.5rem' }}>
                        Shipping Price Comparison
                    </Title>
                    <Subtitle>Optimize and Accelerate Your Supply Chain Operations</Subtitle>
                    <Description>Get instant shipping quotes from multiple carriers. Scale with confidence, empowered by AI.</Description>
                </MainTitle>
            </HeaderSection>

            <MainContent>
                <ContentWrapper hasResults={shippingOptions.length > 0}>
                    <FormCard hasResults={shippingOptions.length > 0}>
                    <FormTitle>Send Internationally</FormTitle>
                    <Form form={form} layout="vertical">
                        <FormRow gutter={24}>
                            <Col span={12}>
                                <Form.Item label="Collection From:">
                                    <Select
                                        placeholder="Select a country"
                                        value={originCountry}
                                        onChange={handleOriginCountryChange}
                                        size="large"
                                        style={inputStyles}
                                        showSearch
                                        filterOption={(input, option) =>
                                                String(option?.children || '')
                                                    .toLowerCase()
                                                    .includes(input.toLowerCase())
                                        }
                                        optionFilterProp="children"
                                    >
                                            {countries.map((country) => (
                                            <Option key={country.code} value={country.code}>
                                                {country.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Delivering To:">
                                    <Select
                                        placeholder="Select a country"
                                        value={destinationCountry}
                                        onChange={handleDestinationCountryChange}
                                        size="large"
                                        style={inputStyles}
                                        showSearch
                                        filterOption={(input, option) =>
                                                String(option?.children || '')
                                                    .toLowerCase()
                                                    .includes(input.toLowerCase())
                                        }
                                        optionFilterProp="children"
                                    >
                                            {countries.map((country) => (
                                            <Option key={country.code} value={country.code}>
                                                {country.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </FormRow>

                        <ParcelSection>
                            <ParcelHeader>
                                    <SendingText>I am Sending</SendingText>
                            </ParcelHeader>

                            <ParcelTypeButtons>
                                    <ParcelTypeButton active={true}>Package</ParcelTypeButton>
                            </ParcelTypeButtons>

                            {parcels.map((parcel, index) => (
                                    <ParcelRow key={parcel.id} hasResults={shippingOptions.length > 0}>
                                        <Col span={shippingOptions.length > 0 ? 3 : 4}>
                                        <Form.Item label="Quantity">
                                                <QuantityInput
                                                    hasResults={shippingOptions.length > 0}
                                                type="number"
                                                value={parcel.quantity}
                                                onChange={(e) => handleParcelChange(parcel.id, 'quantity', Number(e.target.value))}
                                                min={1}
                                                    placeholder="1"
                                            />
                                        </Form.Item>
                                    </Col>
                                        <Col span={shippingOptions.length > 0 ? 3 : 4}>
                                        <Form.Item label="Weight">
                                                <WeightInput
                                                    hasResults={shippingOptions.length > 0}
                                                type="number"
                                                value={parcel.weight}
                                                onChange={(e) => handleParcelChange(parcel.id, 'weight', Number(e.target.value))}
                                                min={0}
                                                step={0.1}
                                                    placeholder="0.0"
                                            />
                                        </Form.Item>
                                    </Col>
                                        <Col span={shippingOptions.length > 0 ? 18 : 16}>
                                            <Form.Item label="Dimensions (L × W × H)">
                                                <DimensionsWrapper>
                                                    <DimensionsContainer hasResults={shippingOptions.length > 0}>
                                                        <DimensionInput
                                                            hasResults={shippingOptions.length > 0}
                                                type="number"
                                                value={parcel.length || ''}
                                                onChange={(e) => handleParcelChange(parcel.id, 'length', Number(e.target.value))}
                                                min={0}
                                                step={0.1}
                                                            placeholder="L"
                                                        />
                                                        <DimensionSeparator></DimensionSeparator>
                                                        <DimensionInput
                                                            hasResults={shippingOptions.length > 0}
                                                type="number"
                                                value={parcel.width || ''}
                                                onChange={(e) => handleParcelChange(parcel.id, 'width', Number(e.target.value))}
                                                min={0}
                                                step={0.1}
                                                            placeholder="W"
                                                        />
                                                        <DimensionSeparator></DimensionSeparator>
                                                        <DimensionInput
                                                            hasResults={shippingOptions.length > 0}
                                                type="number"
                                                value={parcel.height || ''}
                                                onChange={(e) => handleParcelChange(parcel.id, 'height', Number(e.target.value))}
                                                min={0}
                                                step={0.1}
                                                            placeholder="H"
                                            />
                                                        <DimensionUnit>CM</DimensionUnit>
                                                    </DimensionsContainer>
                                    {parcels.length > 1 && (
                                                        <SmallDeleteButton type="text" danger onClick={() => handleRemoveParcel(parcel.id)}>
                                                            <span className="material-icons">delete</span>
                                                        </SmallDeleteButton>
                                                    )}
                                                </DimensionsWrapper>
                                            </Form.Item>
                                        </Col>
                                </ParcelRow>
                            ))}

                                <TotalWeight>Total weight for billing: {totalWeight} KG</TotalWeight>

                                <AddParcelButton type="dashed" onClick={handleAddParcel} block icon={<PlusOutlined />}>
                                    Add Another Package
                                </AddParcelButton>
                        </ParcelSection>

                        <GetQuotesButton
                            type="primary"
                            size="large"
                            icon={<SendOutlined />}
                            onClick={handleGetQuotes}
                            loading={loading}
                            disabled={!originCountry || !destinationCountry || totalWeight === 0}
                        >
                            Get Quotes
                        </GetQuotesButton>
                    </Form>
                </FormCard>
                
                {shippingOptions.length > 0 && (
                    <ShippingOptionsSection>
                            {currentResponse?.bestMatch && (
                                <BestMatchSection>
                                    <Title level={3} style={{ color: '#52c41a', marginBottom: '16px' }}>
                                        🏆 Best Match - Lowest Price
                                    </Title>
                                    <BestMatchCard>
                                <Row gutter={16} align="middle">
                                    <Col span={4}>
                                        <CarrierLogo>
                                                    <div
                                                        style={{
                                                            fontSize: '16px',
                                                            fontWeight: 'bold',
                                                            color: '#52c41a',
                                                            textAlign: 'center',
                                                            padding: '8px'
                                                        }}
                                                    >
                                                        {currentResponse.bestMatch.carrierName}
                                                    </div>
                                        </CarrierLogo>
                                    </Col>
                                    <Col span={8}>
                                        <ServiceInfo>
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                                                            {currentResponse.bestMatch.carrierName} Service
                                                        </Text>
                                                    </div>
                                                    <div style={{ marginBottom: '4px' }}>
                                                        <Text type="secondary">📅 Est. delivery: 5-7 Days</Text>
                                                    </div>
                                                    <div>
                                                        <Text type="secondary">⏱️ Fast & Reliable</Text>
                                                    </div>
                                        </ServiceInfo>
                                    </Col>
                                            <Col span={12}>
                                        <PricingColumn>
                                                    <PricingHeader>Shipping Price</PricingHeader>
                                                    <Price style={{ color: '#52c41a', fontSize: '24px' }}>
                                                        {currentResponse.bestMatch.totalPrice?.toFixed(2) || 'N/A'}
                                                    </Price>
                                                    <Button
                                                        type="primary"
                                                        size="large"
                                                        style={{
                                                            marginBottom: '8px',
                                                            backgroundColor: '#52c41a',
                                                            borderColor: '#52c41a',
                                                            width: '100%'
                                                        }}
                                                    >
                                                Place Order
                                            </Button>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                        All taxes and fees included
                                            </Text>
                                        </PricingColumn>
                                    </Col>
                                        </Row>
                                    </BestMatchCard>
                                </BestMatchSection>
                            )}

                            <Title level={3}>All Available Shipping Options</Title>
                            {shippingOptions.map((option, index) => (
                                <ShippingOptionCard key={index}>
                                    <Row gutter={16} align="middle">
                                        <Col span={4}>
                                            <CarrierLogo>
                                                <div
                                                    style={{
                                                        fontSize: '16px',
                                                        fontWeight: 'bold',
                                                        color: '#1890ff',
                                                        textAlign: 'center',
                                                        padding: '8px'
                                                    }}
                                                >
                                                    {option.carrierName}
                                                </div>
                                            </CarrierLogo>
                                        </Col>
                                        <Col span={8}>
                                            <ServiceInfo>
                                                <div style={{ marginBottom: '8px' }}>
                                                    <Text strong style={{ fontSize: '16px' }}>
                                                        {option.service}
                                                    </Text>
                                                </div>
                                                <div style={{ marginBottom: '4px' }}>
                                                    <Text type="secondary">📅 Est. delivery: {option.estimatedDate}</Text>
                                                </div>
                                                <div>
                                                    <Text type="secondary">⏱️ {option.estimatedDays}</Text>
                                                </div>
                                            </ServiceInfo>
                                        </Col>
                                        <Col span={12}>
                                            <PricingColumn>
                                                <PricingHeader>Shipping Price</PricingHeader>
                                                <Price style={{ fontSize: '20px' }}>{option.businessPrice?.toFixed(2) || 'N/A'}</Price>
                                                <Button type="primary" size="large" style={{ marginBottom: '8px', width: '100%' }}>
                                                Place Order
                                            </Button>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    All taxes and fees included
                                            </Text>
                                        </PricingColumn>
                                    </Col>
                                </Row>
                                <Divider />
                                <Row>
                                    <Col span={12}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                <Text type="secondary">🛡️ Insurance: {option.insurance}</Text>
                                                <Text type="secondary">🖨️ Printing Required: {option.printingRequired ? 'Yes' : 'No'}</Text>
                                            </div>
                                            {currentResponse?.carriers.find((c) => c.carrierCode === option.carrierCode)?.dimensionalWeight && (
                                                <div style={{ marginTop: '8px' }}>
                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                        📦 Dimensional Weight:{' '}
                                                        {
                                                            currentResponse.carriers.find((c) => c.carrierCode === option.carrierCode)
                                                                ?.dimensionalWeight
                                                        }{' '}
                                                        kg
                                        </Text>
                                                </div>
                                            )}
                                    </Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                            <Button type="link" style={{ color: '#1890ff' }}>
                                                ✔ More info
                                            </Button>
                                    </Col>
                                </Row>
                            </ShippingOptionCard>
                        ))}
                    </ShippingOptionsSection>
                )}
                </ContentWrapper>
            </MainContent>

            <FooterSection>
                <FooterContent>
                    <Row gutter={[32, 32]} align="middle">
                        <Col xs={24} sm={12} md={8}>
                            <FooterTitle>CargoZone</FooterTitle>
                            <FooterText>Supply Chain Excellence</FooterText>
                            <FooterText>Optimize and Accelerate Your Supply Chain Operations</FooterText>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <FooterTitle>Contact Us</FooterTitle>
                            <FooterText>
                                <PhoneOutlined style={{ marginRight: '8px' }} />
                                +972.52.8929199
                            </FooterText>
                            <FooterText>
                                <MailOutlined style={{ marginRight: '8px' }} />
                                info@cargozone.ai
                            </FooterText>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <FooterTitle>Quick Actions</FooterTitle>
                            <Space direction="vertical" size="small">
                                <Button type="link" onClick={() => handleDirectContact('call')} style={{ padding: 0, color: '#fca80f' }}>
                                    Call Now
                                </Button>
                                <Button type="link" onClick={() => handleDirectContact('whatsapp')} style={{ padding: 0, color: '#fca80f' }}>
                                    WhatsApp
                                </Button>
                                <Button type="link" onClick={() => handleDirectContact('email')} style={{ padding: 0, color: '#fca80f' }}>
                                    Email Us
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                    <Divider />
                    <FooterBottom>
                        <Text style={{ color: 'white' }}>© {new Date().getFullYear()} CargoZone. All Rights Reserved. CargoZone LTD</Text>
                    </FooterBottom>
                </FooterContent>
            </FooterSection>

            <LeadModal
                title={
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffffff', marginBottom: '8px' }}>
                            Ready to optimize your supply chain?
                        </div>
                        <div style={{ fontSize: '16px', color: '#ffffff' }}>Get personalized shipping solutions from our supply chain experts</div>
                    </div>
                }
                open={showLeadModal}
                onCancel={handleModalClose}
                footer={null}
                centered
                width={500}
            >
                <div style={{ padding: '0 20px 20px' }}>
                    <Form form={leadForm} onFinish={handleLeadSubmit} layout="vertical">
                        <Form.Item name="name" rules={[{ required: true, message: 'Please enter your name' }]} label="Your Name" required>
                            <Input placeholder="Enter your full name" style={inputStyles} size="large" />
                    </Form.Item>
                        <Form.Item name="email" label="Email Address (Optional)">
                            <Input placeholder="your.email@company.com" type="email" style={inputStyles} size="large" />
                    </Form.Item>
                        <Form.Item style={{ marginBottom: '24px' }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                block
                                style={{
                                    backgroundColor: '#fca80f',
                                    borderColor: '#fca80f',
                                    height: '48px',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                            Get Started
                        </Button>
                    </Form.Item>
                </Form>

                    <Divider style={{ margin: '24px 0' }}>
                        <Text type="secondary">Or contact us directly</Text>
                    </Divider>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <Button 
                        icon={<PhoneOutlined />} 
                        onClick={() => handleDirectContact('call')}
                            size="large"
                            style={{
                                backgroundColor: '#fca80f',
                                borderColor: '#fca80f',
                                color: 'white',
                                flex: 1
                            }}
                        >
                            Call
                    </Button>
                    <Button 
                        icon={<WhatsAppOutlined />} 
                        onClick={() => handleDirectContact('whatsapp')}
                            size="large"
                            style={{
                                backgroundColor: '#25D366',
                                borderColor: '#25D366',
                                color: 'white',
                                flex: 1
                            }}
                    >
                        WhatsApp
                    </Button>
                    <Button 
                        icon={<MailOutlined />} 
                        onClick={() => handleDirectContact('email')}
                            size="large"
                            style={{
                                backgroundColor: '#1890ff',
                                borderColor: '#1890ff',
                                color: 'white',
                                flex: 1
                            }}
                        >
                            Email
                    </Button>
                    </div>
                </div>
            </LeadModal>
        </Container>
    );
};

const inputStyles = {
    borderRadius: '8px',
    fontSize: '14px'
};

const StyledInput = styled(Input)`
    border-radius: 8px !important;
    border: 1px solid #d9d9d9 !important;
    font-size: 14px !important;
    height: 40px !important;
    
    &:focus,
    &:hover {
        border-color: #40a9ff !important;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
    }
    
    .ant-input-suffix {
        border-radius: 0 8px 8px 0 !important;
    }
`;

const Container = styled.div`
    min-height: 100vh;
    background: white;
    padding: 0;
`;

const HeaderSection = styled.div`
    text-align: center;
    padding: 60px 20px 40px;
    background: white;
`;

const MainTitle = styled.div`
    margin-bottom: 20px;
`;

const Subtitle = styled.div`
    font-size: 1.2rem;
    color: #666;
    margin: 16px 0;
    font-weight: 500;
`;

const Description = styled.div`
    font-size: 1rem;
    color: #888;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
`;

const MainContent = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 20px 60px;
`;

const ContentWrapper = styled.div<{ hasResults: boolean }>`
    display: flex;
    gap: 30px;
    align-items: flex-start;
    width: 100%;

    ${(props) =>
        props.hasResults
            ? `
        flex-direction: row;
        
        @media (max-width: 1200px) {
            gap: 20px;
        }
        
        @media (max-width: 768px) {
            flex-direction: column;
            gap: 20px;
        }
    `
            : `
        flex-direction: column;
        max-width: 600px;
        margin: 0 auto;
    `}

    @media (max-width: 768px) {
        flex-direction: column !important;
        gap: 20px;
    }
`;

const FormCard = styled.div<{ hasResults?: boolean }>`
    background: linear-gradient(135deg, #404a69 0%, #2c3e50 100%);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
    min-width: 600px;

    ${(props) =>
        props.hasResults
            ? `
        width: 600px;
        padding: 30px;
        
        @media (max-width: 1200px) {
            width: 600px;
            padding: 25px;
        }
        
        @media (max-width: 768px) {
            width: 100%;
            min-width: unset;
            padding: 30px;
        }
    `
            : `
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
    padding: 40px;
        
        @media (max-width: 768px) {
            padding: 30px;
        }
    `}

    .ant-select-selector {
        background: rgba(255, 255, 255, 0.95) !important;
        border: 2px solid #e8e8e8 !important;
        border-radius: 8px !important;
        height: 48px !important;
        transition: all 0.3s ease !important;

        &:hover {
            border-color: #fca80f !important;
            box-shadow: 0 0 0 2px rgba(252, 168, 15, 0.2) !important;
        }

        &:focus-within {
            border-color: #fca80f !important;
            box-shadow: 0 0 0 2px rgba(252, 168, 15, 0.3) !important;
        }
    }

    .ant-select-selection-item {
        color: #333 !important;
        font-weight: 500 !important;
    }

    .ant-select-selection-placeholder {
        color: #999 !important;
    }

    .ant-input-group {
        display: flex !important;

        .ant-input:first-child {
            border-right: none !important;
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
        }

        .ant-input:last-child {
            border-left: none !important;
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
            background: rgba(248, 249, 250, 0.95) !important;
            color: #666 !important;
            font-weight: 500 !important;
            cursor: default !important;
        }

        &:hover {
            .ant-input:first-child {
                border-color: #fca80f !important;
            }
            .ant-input:last-child {
                border-color: #fca80f !important;
            }
        }

        &:focus-within {
            .ant-input:first-child {
                border-color: #fca80f !important;
                box-shadow: 0 0 0 2px rgba(252, 168, 15, 0.3) !important;
            }
            .ant-input:last-child {
                border-color: #fca80f !important;
                box-shadow: 0 0 0 2px rgba(252, 168, 15, 0.3) !important;
            }
        }
    }

    .ant-form-item-label > label {
        color: #ffffff !important;
        font-weight: 600 !important;
        font-size: 14px !important;
        text-align: center !important;
        display: block !important;
        margin-bottom: 8px !important;

        @media (max-width: 768px) {
            font-size: 13px !important;
            margin-bottom: 6px !important;
        }

        @media (max-width: 480px) {
            font-size: 12px !important;
            margin-bottom: 4px !important;
        }
    }

    .ant-form-item-label > label.ant-form-item-required::after {
        color: #ff4d4f !important;
    }
`;

const QuantityInput = styled(Input)<{ hasResults?: boolean }>`
    background: rgba(255, 255, 255, 0.95) !important;
    border: none !important;
    border-radius: 12px !important;
    height: ${(props) => (props.hasResults ? '44px' : '48px')} !important;
    font-size: ${(props) => (props.hasResults ? '13px' : '14px')} !important;
    font-weight: 500 !important;
    text-align: center !important;
    transition: all 0.3s ease !important;
    min-width: ${(props) => (props.hasResults ? '50px' : '60px')} !important;

    /* Remove number input spinners */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none !important;
        margin: 0 !important;
    }

    &[type='number'] {
        -moz-appearance: textfield !important;
    }

    &:hover {
        background: rgba(255, 255, 255, 1) !important;
        box-shadow: 0 0 0 3px rgba(252, 168, 15, 0.1) !important;
    }

    &:focus {
        background: rgba(255, 255, 255, 1) !important;
        box-shadow: 0 0 0 3px rgba(252, 168, 15, 0.2) !important;
    }

    @media (max-width: 768px) {
        height: ${(props) => (props.hasResults ? '40px' : '44px')} !important;
        font-size: ${(props) => (props.hasResults ? '12px' : '13px')} !important;
        min-width: ${(props) => (props.hasResults ? '45px' : '50px')} !important;
    }

    @media (max-width: 480px) {
        height: ${(props) => (props.hasResults ? '36px' : '40px')} !important;
        font-size: ${(props) => (props.hasResults ? '11px' : '12px')} !important;
        min-width: ${(props) => (props.hasResults ? '40px' : '45px')} !important;
    }
`;

const WeightInput = styled(Input)<{ hasResults?: boolean }>`
    background: rgba(255, 255, 255, 0.95) !important;
    border: none !important;
    border-radius: 12px !important;
    height: ${(props) => (props.hasResults ? '44px' : '48px')} !important;
    font-size: ${(props) => (props.hasResults ? '13px' : '14px')} !important;
    font-weight: 500 !important;
    text-align: center !important;
    transition: all 0.3s ease !important;
    position: relative !important;
    min-width: ${(props) => (props.hasResults ? '50px' : '60px')} !important;

    /* Remove number input spinners */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none !important;
        margin: 0 !important;
    }

    &[type='number'] {
        -moz-appearance: textfield !important;
    }

    &::after {
        content: 'KG';
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: #fca80f;
        font-weight: 600;
        font-size: 14px;
        pointer-events: none;
    }

    &:hover {
        background: rgba(255, 255, 255, 1) !important;
        box-shadow: 0 0 0 3px rgba(252, 168, 15, 0.1) !important;
    }

    &:focus {
        background: rgba(255, 255, 255, 1) !important;
        box-shadow: 0 0 0 3px rgba(252, 168, 15, 0.2) !important;
    }

    @media (max-width: 768px) {
        height: ${(props) => (props.hasResults ? '40px' : '44px')} !important;
        font-size: ${(props) => (props.hasResults ? '12px' : '13px')} !important;
        min-width: ${(props) => (props.hasResults ? '45px' : '50px')} !important;

        &::after {
            font-size: 12px;
            right: 10px;
        }
    }

    @media (max-width: 480px) {
        height: ${(props) => (props.hasResults ? '36px' : '40px')} !important;
        font-size: ${(props) => (props.hasResults ? '11px' : '12px')} !important;
        min-width: ${(props) => (props.hasResults ? '40px' : '45px')} !important;

        &::after {
            font-size: 11px;
            right: 8px;
        }
    }
`;

const DimensionsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const DimensionsContainer = styled.div<{ hasResults?: boolean }>`
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.95);
    border: none;
    border-radius: 12px;
    height: ${(props) => (props.hasResults ? '44px' : '48px')};
    padding: 0 ${(props) => (props.hasResults ? '12px' : '16px')};
    transition: all 0.3s ease;
    flex: 1;

    &:hover {
        background: rgba(255, 255, 255, 1);
        box-shadow: 0 0 0 3px rgba(252, 168, 15, 0.1);
    }

    &:focus-within {
        background: rgba(255, 255, 255, 1);
        box-shadow: 0 0 0 3px rgba(252, 168, 15, 0.2);
    }
`;

const DimensionInput = styled(Input)<{ hasResults?: boolean }>`
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    height: ${(props) => (props.hasResults ? '36px' : '40px')} !important;
    font-size: ${(props) => (props.hasResults ? '13px' : '14px')} !important;
    font-weight: 500 !important;
    text-align: center !important;
    padding: 0 ${(props) => (props.hasResults ? '6px' : '8px')} !important;
    flex: 1 !important;

    /* Remove number input spinners */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none !important;
        margin: 0 !important;
    }

    &[type='number'] {
        -moz-appearance: textfield !important;
    }

    &:focus {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
    }

    &::placeholder {
        color: #ccc !important;
        font-weight: 400 !important;
    }
`;

const DimensionSeparator = styled.span`
    width: 1px;
    height: 24px;
    background: #e8e8e8;
    margin: 0 8px;
    user-select: none;
`;

const DimensionUnit = styled.span`
    color: #fca80f;
    font-weight: 600;
    font-size: 14px;
    margin-left: 12px;
    user-select: none;
`;

const TotalWeight = styled.div`
    text-align: center;
    margin: 20px 0;
    padding: 16px;
    background: rgba(252, 168, 15, 0.1);
    border: 2px solid rgba(252, 168, 15, 0.3);
    border-radius: 12px;
    color: #fca80f;
    font-size: 18px;
    font-weight: 700;
`;

const SendingText = styled(Text)`
    font-size: 20px !important;
    font-weight: 700 !important;
    color: #fca80f !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
`;

const SmallDeleteButton = styled(Button)`
    height: 32px !important;
    width: 32px !important;
    min-width: 32px !important;
    border: 1px solid #ff4d4f !important;
    background: rgba(255, 77, 79, 0.1) !important;
    color: #ff4d4f !important;
    border-radius: 6px !important;
    transition: all 0.3s ease !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;

    .material-icons {
        font-size: 16px !important;
    }

    &:hover {
        background: #ff4d4f !important;
        color: white !important;
        transform: scale(1.1) !important;
        box-shadow: 0 2px 8px rgba(255, 77, 79, 0.3) !important;
    }

    &:focus {
        background: #ff4d4f !important;
        color: white !important;
    }
`;

const DeleteParcelButton = styled(Button)`
    height: 40px !important;
    border: 2px solid #ff4d4f !important;
    background: rgba(255, 77, 79, 0.1) !important;
    color: #ff4d4f !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    border-radius: 8px !important;
    margin-top: 30px !important;
    transition: all 0.3s ease !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 6px !important;

    &:hover {
        background: #ff4d4f !important;
        color: white !important;
        transform: translateY(-2px) !important;
        box-shadow: 0 4px 12px rgba(255, 77, 79, 0.3) !important;
    }

    &:focus {
        background: #ff4d4f !important;
        color: white !important;
    }
`;

const AddParcelButton = styled(Button)`
    height: 48px !important;
    border: 2px dashed rgba(252, 168, 15, 0.5) !important;
    background: rgba(252, 168, 15, 0.05) !important;
    color: #fca80f !important;
    font-weight: 600 !important;
    font-size: 16px !important;
    border-radius: 12px !important;
    margin: 16px 0 !important;
    transition: all 0.3s ease !important;

    &:hover {
        border-color: #fca80f !important;
        background: rgba(252, 168, 15, 0.1) !important;
        color: #fca80f !important;
        transform: translateY(-2px) !important;
        box-shadow: 0 4px 12px rgba(252, 168, 15, 0.2) !important;
    }

    &:focus {
        border-color: #fca80f !important;
        background: rgba(252, 168, 15, 0.1) !important;
        color: #fca80f !important;
    }
`;

const FormTitle = styled.div`
    font-size: 24px;
    font-weight: bold;
    color: #fca80f;
    text-align: center;
    margin-bottom: 30px;
`;

const FormRow = styled(Row)`
    margin-bottom: 24px;
`;

const ParcelSection = styled.div`
    background: rgba(255, 255, 255, 0.1);
    padding: 24px;
    border-radius: 8px;
    margin-bottom: 24px;
`;

const ParcelHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const ParcelTypeButtons = styled.div`
    margin-bottom: 16px;
`;

const ParcelTypeButton = styled(Button)<{ active: boolean }>`
    background: ${(props) => (props.active ? '#fca80f' : 'rgba(255, 255, 255, 0.2)')};
    color: ${(props) => (props.active ? 'white' : 'white')};
    border: 1px solid #fca80f;
    border-radius: 6px;
    margin-right: 8px;
    
    &:hover {
        background: ${(props) => (props.active ? '#e6940a' : 'rgba(255, 255, 255, 0.3)')};
        color: white;
        border-color: #fca80f;
    }
`;

const ParcelRow = styled(Row)<{ hasResults?: boolean }>`
    margin-bottom: ${(props) => (props.hasResults ? '12px' : '16px')};
    
    .ant-col:first-child {
        padding-right: ${(props) => (props.hasResults ? '8px' : '12px')};
    }
    
    .ant-col:nth-child(2) {
        padding-left: ${(props) => (props.hasResults ? '8px' : '12px')};
        padding-right: ${(props) => (props.hasResults ? '8px' : '12px')};
    }

    .ant-col:nth-child(3) {
        padding-left: ${(props) => (props.hasResults ? '8px' : '12px')};
    }
`;

const GetQuotesButton = styled(Button)`
    width: 100%;
    height: 50px;
    background: #fca80f;
    border-color: #fca80f;
    font-size: 16px;
    font-weight: 500;
    color: white;
    
    &:hover {
        background: #e6940a;
        border-color: #e6940a;
        color: white;
    }
`;

const ShippingOptionsSection = styled.div`
    flex: 1;
    min-width: 0;
    max-width: calc(100% - 630px);

    @media (max-width: 1200px) {
        max-width: calc(100% - 630px);
    }

    @media (max-width: 768px) {
        max-width: 100%;
        width: 100%;
    }
`;

const ShippingOptionCard = styled(Card)`
    margin-bottom: 16px;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    border: 1px solid #f0f0f0;
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
    }

    .ant-card-body {
        padding: 20px;
    }
`;

const CarrierLogo = styled.div`
    font-weight: bold;
    color: #1890ff;
    text-align: center;
`;

const ServiceInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PricingColumn = styled.div<{ private?: boolean }>`
    background: ${(props) => (props.private ? '#fff3cd' : '#e8f5e8')};
    padding: 16px;
    border-radius: 6px;
    text-align: center;
`;

const PricingHeader = styled.div`
    font-weight: 500;
    margin-bottom: 8px;
`;

const Price = styled.div`
    font-size: 18px;
    font-weight: bold;
    color: #1890ff;
    margin-bottom: 12px;
`;

const BestMatchSection = styled.div`
    margin-bottom: 30px;
`;

const BestMatchCard = styled(Card)`
    border: 2px solid #52c41a;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(82, 196, 26, 0.3);
    background: linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%);
    margin-bottom: 20px;
    overflow: hidden;

    .ant-card-body {
        padding: 20px;
    }
`;

const FooterSection = styled.div`
    padding-top: 300px;
    background-color: #404a69;
    background-image: url('https://cargozone.ai/wp-content/uploads/2020/11/seo3-sectionbg2226.png');
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;
`;

const FooterContent = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
`;

const FooterTitle = styled.div`
    font-size: 18px;
    font-weight: bold;
    color: white;
    margin-bottom: 16px;
`;

const FooterText = styled.div`
    color: white;
    margin-bottom: 8px;
    line-height: 1.6;
`;

const FooterBottom = styled.div`
    text-align: center;
    margin-top: 20px;
    color: white;
`;

const LeadModal = styled(Modal)`
    .ant-modal-header {
        background: #404a69;
        color: white;
    }
    
    .ant-modal-title {
        color: white;
    }
    
    .ant-modal-close {
        color: white;
    }
`;

export default ShippingEstimatesPage;
